import { useEffect, useState } from 'react';
import { Frame, Navigation, Banner, Box } from '@shopify/polaris';
import {
  HomeIcon,
  ProductIcon,
  SettingsIcon,
  UploadIcon,
  NoteIcon,
  SearchIcon,
  CategoriesIcon,
  RefreshIcon,
} from '@shopify/polaris-icons';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Home } from './pages/Home';
import { VehiclesList } from './pages/VehiclesList';
import { VehicleDetail } from './pages/VehicleDetail';
import { BoltPatternsList } from './pages/BoltPatternsList';
import { WheelsList } from './pages/WheelsList';
import { WheelDetail } from './pages/WheelDetail';
import { ImportsList } from './pages/ImportsList';
import ImportsDashboard from './pages/ImportsDashboard';
import { ImportDetail } from './pages/ImportDetail';
import { TiresList } from './pages/TiresList';
import { TireDetail } from './pages/TireDetail';
import { TireJobsList } from './pages/TireJobsList';
import { TireJobDetail } from './pages/TireJobDetail';
import { AuditLog } from './pages/AuditLog';
import { FitmentDebug } from './pages/FitmentDebug';
import { VendorConnections } from './pages/VendorConnections';
import { MapCompliance } from './pages/MapCompliance';
import { CollectionsPreview } from './pages/CollectionsPreview';
import { CoverageAudit } from './pages/CoverageAudit';
import { SyncCenter } from './pages/SyncCenter';
import { refreshToken } from './lib/auth';

// Retry forever every 5s once the first attempt fails. Each attempt has a
// 3s timeout in auth.ts, so a failed cycle is ~3s + 5s = 8s wall clock.
// Phase flips to 'unavailable' after the FIRST failure so the UI never
// stays on the 'connecting' banner longer than the timeout.
const RETRY_INTERVAL_MS = 5000;

// Shopify session JWTs expire 60s after issue. Refresh every 45s so the
// cached token in lib/auth.ts is always valid for the next outgoing
// apiFetch — otherwise react-query refetches start 401-ing and the
// in-band retry becomes the only refresh path (and stalls under load).
const KEEPALIVE_INTERVAL_MS = 45_000;

type SessionPhase = 'connecting' | 'ready' | 'unavailable';

function useSessionPhase(): SessionPhase {
  const [phase, setPhase] = useState<SessionPhase>('connecting');

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    async function tick() {
      const token = await refreshToken();
      if (cancelled) return;
      if (token) {
        console.info('[App] session token acquired');
        setPhase('ready');
        // Stay alive — re-fetch a fresh JWT before the 60s expiry so the
        // cached token in lib/auth.ts is always valid for the next request.
        timer = window.setTimeout(tick, KEEPALIVE_INTERVAL_MS);
        return;
      }
      // First (and every subsequent) failure: flip to unavailable so the
      // UI shows the warning banner and renders the rest of the app. Then
      // schedule another retry — never gives up.
      setPhase((prev) => (prev === 'ready' ? 'ready' : 'unavailable'));
      timer = window.setTimeout(tick, RETRY_INTERVAL_MS);
    }

    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return phase;
}

function SessionBanner({ phase }: { phase: SessionPhase }) {
  if (phase === 'ready') return null;
  if (phase === 'connecting') {
    return (
      <Box paddingBlockStart="400" paddingInlineStart="400" paddingInlineEnd="400">
        <Banner tone="info" title="Connecting to Shopify…">
          <p>Connecting to Shopify…</p>
        </Banner>
      </Box>
    );
  }
  return (
    <Box paddingBlockStart="400" paddingInlineStart="400" paddingInlineEnd="400">
      <Banner tone="warning" title="Shopify session unavailable">
        <p>Shopify session token unavailable. Admin API actions may fail.</p>
      </Banner>
    </Box>
  );
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const phase = useSessionPhase();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (phase === 'ready') {
      console.info('[App] Shopify session token acquired — refetching queries.');
      queryClient.invalidateQueries();
    }
  }, [phase, queryClient]);

  const nav = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={[
          { label: 'Home', icon: HomeIcon, url: '/',
            selected: location.pathname === '/', onClick: () => navigate('/') },
          { label: 'Sync Center', icon: RefreshIcon, url: '/sync-center',
            selected: location.pathname.startsWith('/sync-center'), onClick: () => navigate('/sync-center') },
          { label: 'Vehicles', icon: ProductIcon, url: '/vehicles',
            selected: location.pathname.startsWith('/vehicles'), onClick: () => navigate('/vehicles') },
          { label: 'Wheels', icon: SettingsIcon, url: '/wheels',
            selected: location.pathname.startsWith('/wheels'), onClick: () => navigate('/wheels') },
          { label: 'Tires', icon: CategoriesIcon, url: '/tires',
            selected: location.pathname === '/tires' || (location.pathname.startsWith('/tires/') && !location.pathname.startsWith('/tires/jobs')),
            onClick: () => navigate('/tires') },
          { label: 'Tire imports', icon: UploadIcon, url: '/tires/jobs',
            selected: location.pathname.startsWith('/tires/jobs'),
            onClick: () => navigate('/tires/jobs') },
          { label: 'Imports dashboard', icon: UploadIcon, url: '/imports/dashboard',
            selected: location.pathname === '/imports/dashboard', onClick: () => navigate('/imports/dashboard') },
          { label: 'Imports', icon: UploadIcon, url: '/imports',
            selected: location.pathname === '/imports' || location.pathname.startsWith('/imports/') && location.pathname !== '/imports/dashboard',
            onClick: () => navigate('/imports') },
          { label: 'Fitment debug', icon: SearchIcon, url: '/fitment-debug',
            selected: location.pathname.startsWith('/fitment-debug'), onClick: () => navigate('/fitment-debug') },
          { label: 'Vendor connections', icon: SettingsIcon, url: '/vendor-connections',
            selected: location.pathname.startsWith('/vendor-connections'), onClick: () => navigate('/vendor-connections') },
          { label: 'MAP compliance', icon: SettingsIcon, url: '/map-compliance',
            selected: location.pathname.startsWith('/map-compliance'), onClick: () => navigate('/map-compliance') },
          { label: 'Collections preview', icon: SettingsIcon, url: '/collections-preview',
            selected: location.pathname.startsWith('/collections-preview'), onClick: () => navigate('/collections-preview') },
          { label: 'Bolt patterns', icon: SettingsIcon, url: '/bolt-patterns',
            selected: location.pathname.startsWith('/bolt-patterns'), onClick: () => navigate('/bolt-patterns') },
          { label: 'Audit log', icon: NoteIcon, url: '/audit',
            selected: location.pathname === '/audit', onClick: () => navigate('/audit') },
          { label: 'Coverage audit', icon: SearchIcon, url: '/coverage-audit',
            selected: location.pathname.startsWith('/coverage-audit'), onClick: () => navigate('/coverage-audit') },
        ]}
      />
    </Navigation>
  );

  return (
    <Frame navigation={nav}>
      <SessionBanner phase={phase} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/vehicles" element={<VehiclesList />} />
        <Route path="/vehicles/new" element={<VehicleDetail mode="create" />} />
        <Route path="/vehicles/:id" element={<VehicleDetail mode="edit" />} />
        <Route path="/bolt-patterns" element={<BoltPatternsList />} />
        <Route path="/wheels" element={<WheelsList />} />
        <Route path="/wheels/:id" element={<WheelDetail />} />
        <Route path="/imports" element={<ImportsList />} />
        <Route path="/imports/dashboard" element={<ImportsDashboard />} />
        <Route path="/imports/:id" element={<ImportDetail />} />
        <Route path="/tires" element={<TiresList />} />
        <Route path="/tires/jobs" element={<TireJobsList />} />
        <Route path="/tires/jobs/:id" element={<TireJobDetail />} />
        <Route path="/tires/:id" element={<TireDetail />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route path="/fitment-debug" element={<FitmentDebug />} />
        <Route path="/fitment-debug/:id" element={<FitmentDebug />} />
        <Route path="/vendor-connections" element={<VendorConnections />} />
        <Route path="/map-compliance" element={<MapCompliance />} />
        <Route path="/collections-preview" element={<CollectionsPreview />} />
        <Route path="/coverage-audit" element={<CoverageAudit />} />
        <Route path="/sync-center" element={<SyncCenter />} />
      </Routes>
    </Frame>
  );
}
